import Icon from '@ant-design/icons';
import styled from '@emotion/styled';
import { Avatar, Button } from 'antd';
import { useIsStoreAliasFirst } from 'hooks/business/store/useStoreName';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import dayjs from 'lib/dayjs';
import { HTMLAttributes, memo } from 'react';
import { useTranslate } from '@tolgee/react';
import { ReturnOrderListItemV2 } from 'services/api/refund';
import Copy from 'views/Conversation/Sider/Copy';
import { ReactComponent as AvatarSvg } from 'assets/icons/avatar.svg';
import { ReactComponent as ForwardSvg } from 'assets/icons/forward.svg';
import ContactBuyerBtn from './ContactBuyerBtn';
import LazadaReturnItems from './LazadaReturnItems';
import { goPlatform } from '../utils/store';
import { OrderRemark } from './OrderRemark';
import { toUpperCase } from 'utils/toLowerCase';

const Container = styled.div`
  padding: 16px 24px;
  background: #fff;
  border-radius: 2px;
  > div {
    display: flex;
  }
  > div > * {
    flex: 1 0 0;
  }
  > div {
    dl {
      display: flex;
      margin: 0;
      line-height: 24px;
      > dt {
        font-weight: 400;
        color: #84868c;
        white-space: nowrap;
      }
      > dd {
        margin-bottom: 0;
        > span {
          margin-right: 4px;
        }
      }
      button {
        margin-left: 8px;
      }
      > span {
        margin: -2px 0 0 4px;
      }
    }
  }
  > section {
    margin-top: 16px;
    background: #fbfcfd;
    border: solid 1px #f0f2f5;
    border-radius: 2px;
  }
`;

const Line1 = styled.div`
  display: flex;
  justify-content: space-between;
  > * {
    flex: 0 0 auto !important;
  }
`;

export interface LazadaReturnProps extends HTMLAttributes<HTMLDivElement> {
  returnOrder: ReturnOrderListItemV2;
  conversation?: Conversation.Conversation;
  onRefresh: () => void;
}

function LazadaReturn(props: LazadaReturnProps) {
  const { returnOrder, conversation, onRefresh, ...otherProps } = props;

  const {
    region,
    storeName,
    storeAlias,
    orderSn,
    returnSn,
    tradeCreateTimeMs,
    returnCreateTime,
    buyerNick,
    internalStoreId,
    internalTradeId,
    platform,
    lechatSellerMemos,
    externalBuyerId,
    externalStoreId,
  } = returnOrder;

  const { t } = useTranslate();

  const operatable = useGetPermission()('操作弃单', {
    platform: 'LAZADA',
    region,
    storeId: Number(internalStoreId),
  } as any);

  const isStoreAliasFirst = useIsStoreAliasFirst('LAZADA', region);

  const line1 = (
    <Line1>
      <dl>
        <dt>{t('marketing.店铺')}：</dt>
        <dd>{isStoreAliasFirst ? storeAlias || storeName : storeName}</dd>
      </dl>
      {operatable && (
        <Button type="text" size="small" onClick={() => goPlatform(internalStoreId)}>
          {t('tableColumn.联系lazada客服')}
          <Icon component={ForwardSvg} />
        </Button>
      )}
    </Line1>
  );

  const line2 = (
    <div>
      <div>
        <dl>
          <dt>{t('translation.订单编号')}：</dt>
          <dd>{orderSn}</dd>
          <Copy
            text={orderSn}
            successMessage={t('trade.复制成功')}
            failedMessage={t('trade.复制失败')}
          />
        </dl>
        <dl>
          <dt>{t('ViewRefund.退货单编号')}：</dt>
          <dd>{returnSn}</dd>
          <Copy
            text={returnSn}
            successMessage={t('trade.复制成功')}
            failedMessage={t('trade.复制失败')}
          />
        </dl>
      </div>
      <div>
        <dl>
          <dt>{t('ViewRefund.订购日期')}：</dt>
          <dd>{dayjs(Number(tradeCreateTimeMs)).format('YYYY-MM-DD HH:mm:ss')}</dd>
        </dl>
        <dl>
          <dt>{t('ViewRefund.退货订单日期')}：</dt>
          <dd>{dayjs(Number(returnCreateTime)).format('YYYY-MM-DD HH:mm:ss')}</dd>
        </dl>
      </div>
      <div>
        <dl>
          <dt>{t('cancelType.buyer')}：</dt>
          <dd>
            {conversation?.buyerAvatar ? (
              <Avatar size={18} src={conversation?.buyerAvatar} />
            ) : (
              <Icon style={{ fontSize: '18px' }} component={AvatarSvg} />
            )}
            {buyerNick}
            {operatable && (
              <ContactBuyerBtn
                orderSn={orderSn}
                region={toUpperCase(region)}
                externalBuyerId={externalBuyerId}
                externalStoreId={externalStoreId}
                buyerNick={buyerNick}
                returnSn={returnSn}
                type="refund"
                onClose={onRefresh}
                conversation={conversation}
                size="small"
              />
            )}
          </dd>
        </dl>
      </div>
    </div>
  );

  const items = <LazadaReturnItems returnOrder={returnOrder} onRefresh={onRefresh} />;

  return (
    <Container {...otherProps}>
      {line1}
      {line2}
      <div>
        <OrderRemark
          content={lechatSellerMemos ?? ''}
          internalTradeId={internalTradeId}
          platform={platform as General.PlatformUpperCaseTunnel}
          showEdit
          onRefresh={onRefresh}
        />
      </div>
      {items}
    </Container>
  );
}

export default memo(LazadaReturn);
